import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import cookieManager from './services/cookieManager';
// Remove toast css import
// import 'react-toastify/dist/ReactToastify.css';
// Remove ToastContainer import
// import { ToastContainer } from 'react-toastify';

import Navbar from './components/Navbar';
import CookieConsent from './components/CookieConsent';
import Login from './pages/Login';
import Register from './pages/Register';
import EmailVerification from './pages/EmailVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import Profile from './pages/Profile';
import ProfileCreator from './pages/ProfileCreator';
import AdminManagement from './pages/AdminManagement';
// import ProfileListPage from './pages/ProfileListPage'; // Removed import
import AdminProfileList from './pages/admin/AdminProfileList.jsx';
import UserProfile from './pages/UserProfile';
import ViewAllUsers from './pages/admin/ViewAllUsers'; // Corrected import
import AdminUserDetails from './pages/admin/AdminUserDetails'; // Added import
import AboutPage from './pages/AboutPage'; // Added import
import ContactPage from './pages/ContactPage'; // Added import
import AdminQrMapping from './pages/admin/AdminQrMapping'; // Added import
import QrClaimPage from './pages/QrClaimPage'; // Added import
import QrRedirectPage from './pages/QrRedirectPage'; // Added import
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage'; // Added import
import TelegramConfig from './pages/admin/TelegramConfig'; // Added import
import CoordinatesGuide from './pages/CoordinatesGuide'; // Added import
import SiteVisitsAnalytics from './pages/admin/SiteVisitsAnalytics'; // Added import
import AdminComments from './pages/admin/AdminComments'; // Added import
import AdminContactMessages from './pages/admin/AdminContactMessages';
import FAQPage from './pages/FAQPage';

// New page imports
import ProductsPage from './pages/ProductsPage';
import CheckoutPage from './pages/CheckoutPage';
import MyOrdersPage from './pages/MyOrdersPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import AdminOrderManagement from './pages/admin/AdminOrderManagement';
import ProtectedRoute from './components/ProtectedRoute'; // Added import

// Placeholder for the new page
// const ViewAllUsers = () => <div>View All Users Page (Placeholder)</div>; // Removed placeholder

function App() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const path = location.pathname;
  const lang = path.split('/')[1];

  useEffect(() => {
    // Initialize cookie manager on app start
  }, []);

  // Set i18next language based on URL path
  useEffect(() => {
    if (['mk', 'al', 'en', 'tr', 'hr', 'sr', 'de'].includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    }
  }, [lang, i18n]);

  // If no language is specified, redirect to Macedonian
  if (!['mk', 'al', 'en', 'tr', 'hr', 'sr', 'de'].includes(lang)) {
    // Special handling for QR routes - don't redirect if it's a QR code route
    if (path.startsWith('/qr/')) {
      // This is a QR route without language prefix, let it pass through
    } else {
      return <Navigate to={`/mk${path}`} replace />;
    }
  }

  // Check if current route is a profile view (should not show navbar)
  const isProfileView = path.match(/\/[a-z]{2}\/profile\/[A-Za-z0-9]{5}$/);

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-white">
        {!isProfileView && <Navbar />}
        {/* Remove ToastContainer */}
        {/* <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light" 
      /> */}
      <main>
        <Routes>
          {/* QR code redirect route without language prefix (for engraved QR codes) */}
          <Route path="/qr/:code" element={<QrRedirectPage />} />
          
          <Route path="/:lang" element={<Home />} />
          <Route path="/:lang/login" element={<Login />} />
          <Route path="/:lang/register" element={<Register />} />
          <Route path="/:lang/verify" element={<EmailVerification />} />
          <Route path="/:lang/forgot-password" element={<ForgotPassword />} />
          <Route path="/:lang/reset-password" element={<ResetPassword />} />
          <Route path="/:lang/about" element={<AboutPage />} />
          <Route path="/:lang/contact" element={<ContactPage />} />
          <Route path="/:lang/faq" element={<FAQPage />} />
          <Route path="/:lang/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/:lang/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/:lang/coordinates-guide" element={<CoordinatesGuide />} />
          <Route path="/coordinates-guide" element={<CoordinatesGuide />} />
          <Route path="/:lang/profile/:id" element={<Profile />} />
          <Route path="/:lang/create-profile" element={<ProtectedRoute><ProfileCreator /></ProtectedRoute>} />
          <Route path="/:lang/edit-profile/:profileId" element={<ProtectedRoute><ProfileCreator /></ProtectedRoute>} />
          <Route path="/:lang/admin" element={<ProtectedRoute><AdminManagement /></ProtectedRoute>} />
          <Route path="/:lang/admin/profiles" element={<ProtectedRoute><AdminProfileList /></ProtectedRoute>} />
          <Route path="/:lang/admin/users" element={<ProtectedRoute><ViewAllUsers /></ProtectedRoute>} />
          <Route path="/:lang/admin/user-details/:userId" element={<ProtectedRoute><AdminUserDetails /></ProtectedRoute>} />
          <Route path="/:lang/admin/orders" element={<ProtectedRoute><AdminOrderManagement /></ProtectedRoute>} />
          <Route path="/:lang/admin/qr-mapping" element={<ProtectedRoute><AdminQrMapping /></ProtectedRoute>} />
          <Route path="/:lang/admin/telegram-config" element={<ProtectedRoute><TelegramConfig /></ProtectedRoute>} />
          <Route path="/:lang/admin/site-visits" element={<ProtectedRoute><SiteVisitsAnalytics /></ProtectedRoute>} />
          <Route path="/:lang/admin/comments" element={<ProtectedRoute><AdminComments /></ProtectedRoute>} />
          <Route path="/:lang/admin/contact-messages" element={<ProtectedRoute><AdminContactMessages /></ProtectedRoute>} />
          <Route path="/:lang/my-profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
          <Route path="/:lang/qr-claim" element={<QrClaimPage />} />
          <Route path="/:lang/qr/:code" element={<QrRedirectPage />} />

          {/* Product and Order Routes */}
          <Route path="/:lang/products" element={<ProductsPage />} />
          <Route path="/:lang/checkout/:productId" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/:lang/my-orders" element={<ProtectedRoute><MyOrdersPage /></ProtectedRoute>} />
          <Route path="/:lang/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
        </Routes>
      </main>
      <CookieConsent />
    </div>
    </HelmetProvider>
  );
}

export default App;
